import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const empty = { code: "", description: "", discount_type: "percent", discount_value: 10, min_order: 0, is_active: true };

export default function Coupons() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/coupons").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code) return toast.error("Code required");
    try { await api.post("/coupons", { ...form, code: form.code.toUpperCase() }); toast.success("Created"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/coupons/${id}`); load(); };

  return (
    <>
      <PageHeader
        eyebrow="Promotions"
        title="Coupons"
        subtitle="Run flat or percentage discounts. Customers can apply them at the POS."
        actions={<Button onClick={() => setOpen(true)} className="btn-primary" data-testid="coupon-add-btn"><Plus size={16} weight="bold"/> New coupon</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger">
        {list.map((c) => (
          <div key={c.id} className="bento p-5 flex justify-between items-start" data-testid={`coupon-${c.id}`}>
            <div>
              <div className="label-eyebrow">{c.is_active ? "Active" : "Inactive"}</div>
              <div className="font-display font-extrabold text-2xl text-[var(--brand)] mt-2">{c.code}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">
                {c.discount_type === "percent" ? `${c.discount_value}% off` : `${fmtMoney(c.discount_value)} off`}
                {c.min_order > 0 ? ` · min ${fmtMoney(c.min_order)}` : ""}
              </div>
              {c.description && <div className="text-xs mt-2">{c.description}</div>}
            </div>
            <button onClick={() => del(c.id)} className="p-1.5 rounded text-[var(--brand)] hover:bg-[var(--bg-secondary)]" data-testid={`coupon-del-${c.id}`}><Trash size={14}/></button>
          </div>
        ))}
        {list.length === 0 && <div className="bento p-8 text-center text-sm text-[var(--text-secondary)] col-span-full">No coupons yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="coupon-dialog">
          <DialogHeader><DialogTitle>New coupon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1" data-testid="coupon-form-code"/>
            </div>
            <div>
              <Label className="label-eyebrow">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="coupon-form-desc"/>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="label-eyebrow">Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="coupon-form-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent</SelectItem>
                    <SelectItem value="flat">Flat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Value</Label>
                <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value || 0) })} className="mt-1" data-testid="coupon-form-value"/>
              </div>
              <div>
                <Label className="label-eyebrow">Min order</Label>
                <Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: parseFloat(e.target.value || 0) })} className="mt-1" data-testid="coupon-form-min"/>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="btn-primary" data-testid="coupon-form-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
